import {
    ForbiddenError,
    HttpError
} from "components/ServerValidationErrors";

import { VodafoneStockTransferImportApiUrl } from "env-config";
import { acquireAccessToken } from "./access-token-service";

export type RequestError = {
    detail: string;
    type: string;
};

export type RequestItemError = {
    reference: string;
    detail: string;
    type: string;
};

export type RequestItemAction = {
    reference: string;
    action: string;
    status: RequestItemActionStatus;
};

export type Link = {
    rel: string;
    href: string;
    method: string;
};

export type ImportValidationFailureResponse = {
    requestErrors: RequestError[];
    requestItemErrors: RequestItemError[];
};

export class ValidationError extends HttpError {
    data: ImportValidationFailureResponse | undefined;
    constructor(status: number, message?: string, data?: ImportValidationFailureResponse) {
        super(status, message);
        this.data = data;
    }
}

export type StockTransferFileProcessingPhase = "PreCheck" | "Import";

export type StockTransferFileProcessingOutcome = "Succeeded" | "Failed";

export type RequestItemActionStatus = "Planned" | "Succeeded" | "Failed" | "Cancelled";

export type StockTransferFileImportResultsResponse = {
    requestId?: string;
    outcome: StockTransferFileProcessingOutcome;
    requestErrors: RequestError[];
    requestItemErrors: RequestItemError[];
    requestItemActions: RequestItemAction[];
};

export type StatusMonitorResponse = {
    status: "pending" | "complete";
    links: Link[];
};

const baseApiUrl = (): string => VodafoneStockTransferImportApiUrl().replace(/[/]$/, "");

function requireHeader(name: string, response: Response): string {
    const header = response.headers.get(name);
    if (header === null) {
        throw Error(`Response is missing the required header: ${name}`);
    }
    return header;
}

function requireLink(rel: string, links: Link[]) {
    const matches = links.filter((l) => l.rel === rel);
    if (!matches.length) throw Error(`Link not found with relationship: ${rel}`);
    if (matches.length > 1) throw Error(`Multiple links found with relationship: ${rel}`);
    return matches[0];
}

function sleep(delayMilliseconds: number) {
    return new Promise((resolve) => setTimeout(resolve, delayMilliseconds));
}

async function apiAsyncFormPost<TResponse>(
    path: string,
    mounted: React.MutableRefObject<boolean>,
    form: { [key: string]: string | File},
    options: RequestInit = {},
    appendBaseApiUrl = true
): Promise<TResponse> {
    const formData = new FormData();
    for (const key in form) {
        formData.append(key, form[key]);
    }

    const url = appendBaseApiUrl ? `${baseApiUrl()}${path}` : path;
    const accessToken = await acquireAccessToken("api://vodafone-stock-transfer-import-api", "access");
    const authHeaders = { Authorization: `Bearer ${accessToken}` };

    let response = await fetch(url, {
        ...options,
        method: "POST",
        body: formData,
        headers: { ...options.headers, ...authHeaders },
    });

    if (response.status === 202) {
        const statusMonitorUrl = requireHeader("Location", response);
        do {
            const retryAfterSeconds = Number(requireHeader("Retry-After", response));
            await sleep(retryAfterSeconds * 1000);

            response = await fetch(statusMonitorUrl, { headers: authHeaders });
            if (response.status !== 200) break;

            const responseContent = (await response.json()) as StatusMonitorResponse;
            if (responseContent.status === "complete") {
                const resultsUrl = requireLink("results", responseContent.links).href;
                response = await fetch(resultsUrl, { headers: authHeaders });
                break;
            }
        } while (mounted.current);
    }

    if (response.ok) {
        if (response.status === 204) {
            return {} as TResponse;
        }
        return (await response.json()) as TResponse;
    } else if (response.status === 400) {
        throw new ValidationError(
            response.status,
            response.statusText,
            (await response.json()) as ImportValidationFailureResponse
        );
    } else if (response.status === 403) {
        throw new ForbiddenError(response.status, await response.text());
    } else {
        throw new HttpError(response.status, await response.text());
    }
}

async function apiAsyncFetch<TResponse>(path: string, options: RequestInit, appendBaseApiUrl = true): Promise<TResponse> {
    const url = appendBaseApiUrl ? `${baseApiUrl()}${path}` : path;
    const accessToken = await acquireAccessToken("api://vodafone-stock-transfer-import-api", "access");
    const response = await fetch(url, {
        ...options,
        headers: { ...options.headers, Authorization: `Bearer ${accessToken}` },
    });
    if (response.ok) {
        if (response.status === 204) {
            return {} as TResponse;
        }
        const result = await response.json();
        return result as TResponse;
    } else if (response.status === 400) {
        throw new ValidationError(
            response.status,
            response.statusText,
            (await response.json()) as ImportValidationFailureResponse
        );
    } else if (response.status === 403) {
        throw new ForbiddenError(response.status, await response.text());
    } else {
        throw new HttpError(response.status, await response.text());
    }
}

export async function performStockTransferImportFilePreCheck(
    fieldCompanyId: number,
    file: File,
    api: string,
    mounted: React.MutableRefObject<boolean>,
    forceUpload: boolean,
) {
    return apiAsyncFormPost<StockTransferFileImportResultsResponse>(`/imports/${api}`, mounted, {
        fieldCompanyId: fieldCompanyId.toString(),
        file: file,
        stopAfterPhase: "PreCheck",
        forceUpload: forceUpload.toString(),
    });
}

export async function performStockTransferFileImport(
    fieldCompanyId: number,
    file: File,
    api: string,
    mounted: React.MutableRefObject<boolean>,
    forceUpload: boolean
) {
    return apiAsyncFormPost<StockTransferFileImportResultsResponse>(`/imports/${api}`, mounted, {
        fieldCompanyId: fieldCompanyId.toString(),
        file: file,
        stopAfterPhase: "Import",
        forceUpload: forceUpload.toString()
    });
}

export async function getStockTransferFileImportResults(requestId: string) {
    return apiAsyncFetch<StockTransferFileImportResultsResponse>(`/imports/${requestId}/results`, {});
}
